import { useState } from "react";
import { AlertTriangle, LogOut, CalendarClock, Zap, Check, X, Trash2, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PasswordInput from "./PasswordInput";
import PaymentMethodsBadge from "./PaymentMethodsBadge";
import { Logo } from "./Logo";

/**
 * Full-page lock overlay shown when /api/merchant/me returns
 * `subscriptionRequired: true` (paid mode is ON and the establishment
 * does not currently hold an active subscription OR a valid ponctuel
 * queue).
 *
 * Spec utilisateur 2026-02 (fork courant) — l'établissement doit choisir
 * entre 2 modes :
 *   • Abonnement 9,90 €/mois TTC — accès illimité tant que l'abonnement
 *     est valide.
 *   • Mode ponctuel 7,90 €/file (24 h) — paiement à la demande, file
 *     valable 24 h puis fermeture automatique.
 *
 * Le choix N'EST PAS définitif : à chaque échéance (fin d'abonnement
 * payé OU fermeture de la file ponctuelle), l'établissement retrouve
 * l'intégralité des options.
 *
 * Spec utilisateur fork 25.8 — un troisième chemin de sortie a été
 * ajouté : « Supprimer mon compte ». Permet à un établissement qui a
 * créé son compte par erreur (ou ne souhaite pas souscrire) de partir
 * proprement. Le backend gère déjà la suppression immédiate dans cet
 * état (pas d'abonnement actif), comme si on était en période gratuite.
 * Fix bonus : le bouton « Se déconnecter » utilisait des clés
 * localStorage obsolètes (`token`/`role`) et ne purgait donc rien —
 * on passe par `useAuth().logout()` qui nettoie la vraie clé
 * `bypassgo_token` + l'état React.
 */
export default function SubscriptionLockScreen({ commerce, onSubscribe, onPonctuel, onSimulateSubscription, onSimulatePonctuel }) {
  const nav = useNavigate();
  const { logout } = useAuth();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      /* never throws but be safe */
    }
    nav("/", { replace: true });
  };

  const resetDelete = () => {
    setDeleteOpen(false);
    setConfirming(false);
    setPassword("");
    setDeleting(false);
  };

  const submitDelete = async () => {
    if (!password) { toast.error("Veuillez saisir votre mot de passe."); return; }
    if (!confirming) { setConfirming(true); return; }
    setDeleting(true);
    try {
      await api.post("/merchant/account/delete", { password });
      toast.success("Votre compte a bien été supprimé.", { duration: 6000 });
      try { await logout(); } catch { /* noop */ }
      nav("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la suppression du compte.");
      setDeleting(false);
      setConfirming(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/85 backdrop-blur-sm flex items-stretch sm:items-center justify-center p-2 sm:p-6 overflow-y-auto"
      data-testid="subscription-lock-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sub-lock-title"
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl border-2 border-brand bg-white shadow-2xl overflow-hidden flex flex-col min-h-[calc(100vh-1rem)] sm:min-h-0 my-0 sm:my-6"
        data-testid="subscription-lock-card"
      >
        {/* Top accent bar */}
        <div aria-hidden="true" className="h-1 sm:h-2 bg-gradient-to-r from-brand via-[#F18F18] to-destructive" />

        <div className="p-4 sm:p-8 text-center flex flex-col flex-1">
          {/* BypassGo logo en-tête (spec utilisateur fork 25.10 — maquette
             ChatGPT) : remplace le pictogramme cadenas par le « B »
             officiel pour un en-tête de marque, plus rassurant que la
             notion d'« accès bloqué ». */}
          <Logo
            variant="symbol"
            height={48}
            className="mx-auto mb-3 sm:mb-4 sm:!h-16"
            data-testid="subscription-lock-logo"
            alt="BypassGo"
          />

          <h2
            id="sub-lock-title"
            className="font-display text-xl sm:text-3xl font-black uppercase tracking-tight text-ink"
            data-testid="subscription-lock-title"
          >
            Choisissez votre formule
          </h2>

          <span aria-hidden="true" className="mx-auto block mt-2.5 mb-3 sm:mt-3 sm:mb-3 h-[3px] w-12 bg-brand rounded-full" />

          <p className="text-[13px] sm:text-sm text-ink leading-snug sm:leading-relaxed mb-4 sm:mb-5" data-testid="subscription-lock-message">
            {commerce?.name ? (
              <>
                Bonjour <span className="font-bold">{commerce.name}</span>,{" "}
              </>
            ) : null}
            choisissez la formule qui vous convient. Vous pourrez en changer à chaque échéance.
          </p>

          {/* Plan cards — side-by-side starting from the smallest screens
             so the whole paywall fits above the fold on mobile. Le
             container parent est en flex-col flex-1 → on laisse les
             cartes prendre la place disponible verticalement via
             `flex-1` pour un rendu plein-écran sans fenêtre flottante. */}
          <div className="grid grid-cols-2 gap-2.5 sm:gap-3 text-left flex-1">
            {/* Subscription plan */}
            <div
              className="border-2 border-brand bg-brand/5 rounded-xl p-3 sm:p-4 flex flex-col"
              data-testid="plan-card-subscription"
            >
              <div className="flex items-center gap-1.5 mb-2 sm:mb-2">
                <CalendarClock size={15} className="text-brand shrink-0 sm:hidden" />
                <CalendarClock size={18} className="text-brand shrink-0 hidden sm:block" />
                <p className="overline text-[9px] sm:text-[10px] text-brand font-black tracking-[0.14em] sm:tracking-[0.18em]">Abonnement</p>
              </div>
              <p className="font-display text-lg sm:text-xl font-black text-ink leading-tight">
                9,90 €/mois<span className="text-[10px] sm:text-sm font-bold text-subtle"> TTC</span>
              </p>
              <p className="text-[10px] sm:text-[11px] text-subtle mb-2.5 sm:mb-3">Sans engagement</p>
              <ul className="text-[11px] sm:text-xs text-ink space-y-1.5 sm:space-y-1.5 mb-3 sm:mb-4 flex-1">
                <li className="flex items-start gap-1.5">
                  <Check size={12} className="text-brand mt-0.5 shrink-0" /> 1ʳᵉ file illimitée
                </li>
                <li className="flex items-start gap-1.5">
                  <Check size={12} className="text-brand mt-0.5 shrink-0" /> Renouvelable au mois
                </li>
                <li className="flex items-start gap-1.5">
                  <Check size={12} className="text-brand mt-0.5 shrink-0" /> Files suppl. : 4,90 € (24 h)
                </li>
              </ul>
              <button
                onClick={onSubscribe}
                className="h-10 sm:h-11 px-2 sm:px-4 rounded-md bg-brand hover:bg-brand-hover text-white font-display font-bold text-[11px] sm:text-sm uppercase tracking-wider transition-colors"
                data-testid="plan-subscribe-btn"
              >
                9,90 €/mois
              </button>
              {(commerce?.simulatePaymentEnabled ?? commerce?.isTestAccount) && (
                <button
                  onClick={onSimulateSubscription}
                  className="mt-1.5 text-[10px] sm:text-[11px] font-bold text-brand underline decoration-dotted underline-offset-2 hover:text-brand-hover"
                  data-testid="plan-subscribe-simulate-btn"
                >
                  🧪 Simuler
                </button>
              )}
            </div>

            {/* Ponctuel plan */}
            <div
              className="border-2 border-[#F18F18] bg-[#F18F18]/5 rounded-xl p-3 sm:p-4 flex flex-col"
              data-testid="plan-card-ponctuel"
            >
              <div className="flex items-center gap-1.5 mb-2 sm:mb-2">
                <Zap size={15} className="text-[#F18F18] shrink-0 sm:hidden" />
                <Zap size={18} className="text-[#F18F18] shrink-0 hidden sm:block" />
                <p className="overline text-[9px] sm:text-[10px] text-[#F18F18] font-black tracking-[0.14em] sm:tracking-[0.18em]">Ponctuel</p>
              </div>
              <p className="font-display text-lg sm:text-xl font-black text-ink leading-tight">
                7,90 €/file<span className="text-[10px] sm:text-sm font-bold text-subtle"> TTC</span>
              </p>
              <p className="text-[10px] sm:text-[11px] text-subtle mb-2.5 sm:mb-3">Valable 24 h</p>
              <ul className="text-[11px] sm:text-xs text-ink space-y-1.5 sm:space-y-1.5 mb-3 sm:mb-4 flex-1">
                <li className="flex items-start gap-1.5">
                  <Check size={12} className="text-[#F18F18] mt-0.5 shrink-0" /> 1ʳᵉ file 24 h
                </li>
                <li className="flex items-start gap-1.5">
                  <Check size={12} className="text-[#F18F18] mt-0.5 shrink-0" /> Fermeture auto
                </li>
                <li className="flex items-start gap-1.5">
                  <Check size={12} className="text-[#F18F18] mt-0.5 shrink-0" /> Files suppl. : 4,90 € (24 h)
                </li>
              </ul>
              <button
                onClick={onPonctuel}
                className="h-10 sm:h-11 px-2 sm:px-4 rounded-md bg-[#F18F18] hover:bg-[#d97e10] text-white font-display font-bold text-[11px] sm:text-sm uppercase tracking-wider transition-colors"
                data-testid="plan-ponctuel-btn"
              >
                7,90 € — 24 h
              </button>
              {(commerce?.simulatePaymentEnabled ?? commerce?.isTestAccount) && (
                <button
                  onClick={onSimulatePonctuel}
                  className="mt-1.5 text-[10px] sm:text-[11px] font-bold text-[#F18F18] underline decoration-dotted underline-offset-2 hover:text-[#d97e10]"
                  data-testid="plan-ponctuel-simulate-btn"
                >
                  🧪 Simuler
                </button>
              )}
            </div>
          </div>

          {/* Bandeau « moyens de paiement acceptés » — spec utilisateur
             2026-02 (Option A). Rassure l'établissement avant le clic sur
             un des 2 boutons de plan : Stripe Checkout proposera
             ensuite Carte / Apple Pay / Google Pay / PayPal selon le
             support du navigateur. */}
          <PaymentMethodsBadge className="mt-3 sm:mt-4" testId="paywall-payment-methods" />

          {/* Encadré info bleu clair (spec maquette fork 25.10) :
             rassure l'établissement sur la souplesse du modèle (libre
             changement à chaque échéance, sans engagement). */}
          <div
            className="mt-4 sm:mt-5 flex items-start gap-2.5 text-left bg-brand/5 border border-brand/20 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3"
            data-testid="subscription-lock-info-box"
          >
            <Info size={18} className="text-brand shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-[12px] sm:text-sm text-ink leading-snug">
              À chaque échéance, vous pourrez librement choisir entre la formule <strong>Abonnement</strong> et la formule <strong>Ponctuelle</strong>.
            </p>
          </div>

          <div className="mt-3 sm:mt-4 flex justify-center">
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 h-9 sm:h-10 px-3.5 sm:px-4 rounded-md border border-border text-subtle hover:border-ink hover:text-ink font-display font-bold text-[11px] sm:text-xs uppercase tracking-wider transition-colors"
              data-testid="subscription-lock-logout-btn"
            >
              <LogOut size={13} /> Se déconnecter
            </button>
          </div>

          {/* Spec fork 25.8 — suppression de compte. */}
          <div className="mt-2 sm:mt-3 flex justify-center">
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              className="inline-flex items-center gap-1.5 text-[11px] sm:text-[11px] text-subtle hover:text-destructive underline-offset-2 hover:underline transition-colors"
              data-testid="subscription-lock-delete-account-btn"
            >
              <Trash2 size={12} /> Supprimer mon compte
            </button>
          </div>

          <p className="mt-2 sm:mt-3 text-[10px] sm:text-[10px] text-subtle">
            Réf.&nbsp;:&nbsp;
            <span className="font-mono">{commerce?.id?.slice(0, 8) || "—"}</span>
          </p>
        </div>
      </div>

      {deleteOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
          onClick={resetDelete}
          data-testid="subscription-lock-delete-backdrop"
        >
          <div
            className="bg-white border-2 border-destructive max-w-md w-full rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="subscription-lock-delete-modal"
            role="alertdialog"
            aria-labelledby="sub-lock-delete-title"
          >
            <div className="px-5 py-4 border-b border-border bg-destructive/5 flex items-start gap-3">
              <AlertTriangle size={22} className="text-destructive shrink-0 mt-0.5" />
              <h3 id="sub-lock-delete-title" className="font-display text-lg font-black text-ink leading-tight flex-1">
                Supprimer définitivement le compte
              </h3>
              <button
                type="button"
                onClick={resetDelete}
                className="text-subtle hover:text-ink"
                aria-label="Fermer"
                data-testid="subscription-lock-delete-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm text-ink leading-snug">
              <p>
                Vous êtes sur le point de supprimer votre compte établissement{commerce?.name ? <> <strong>« {commerce.name} »</strong></> : null}.
              </p>
              <ul className="text-xs text-subtle list-disc pl-5 space-y-1">
                <li>Toutes vos files et tickets en cours seront annulés.</li>
                <li>Les paramètres, logo et historique seront supprimés.</li>
                <li>Aucun paiement ne sera effectué. Aucune souscription ne sera créée.</li>
                <li>Cette action est <strong className="text-destructive">irréversible</strong>.</li>
              </ul>
              <div>
                <label htmlFor="sub-lock-delete-pwd" className="text-xs font-bold text-ink">
                  Confirmez avec votre mot de passe
                </label>
                <PasswordInput
                  id="sub-lock-delete-pwd"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="h-11 mt-1"
                  data-testid="subscription-lock-delete-password"
                />
              </div>
              {confirming && (
                <p className="text-destructive text-xs font-bold" data-testid="subscription-lock-delete-confirm-note">
                  Cliquez à nouveau sur « Supprimer définitivement » pour confirmer.
                </p>
              )}
            </div>
            <div className="px-5 py-4 border-t border-border flex flex-col-reverse sm:flex-row gap-2 sm:justify-end bg-surface/40">
              <button
                type="button"
                onClick={resetDelete}
                disabled={deleting}
                className="h-10 px-4 rounded-md border border-border text-ink hover:border-ink font-bold text-xs uppercase tracking-wider disabled:opacity-50"
                data-testid="subscription-lock-delete-cancel"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={submitDelete}
                disabled={deleting || !password}
                className="h-10 px-4 rounded-md bg-destructive hover:bg-destructive/90 text-white font-bold text-xs uppercase tracking-wider disabled:opacity-50"
                data-testid="subscription-lock-delete-submit"
              >
                {deleting ? "Suppression…" : confirming ? "Supprimer définitivement" : "Continuer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
